import { computed, inject, Service, signal } from '@angular/core';

import { Capacitor } from '@capacitor/core';

import { environment } from '@env/environment';
import { INST } from '@env/inst-type';
import { platform } from '@env/platform';
import { Guid, SM } from '@models/base/helper-types';
import { Person } from '@models/users/person';
import { Org } from '@models/users/org';
import { Project } from '@models/projects/project';

import { StoreService } from '@services/store.service';
import { getRandomId } from '@root/_models';
// import { CacheService } from '@services/cache.service';

@Service()
export class BaseService {

    readonly inst = INST;
    readonly platform = Capacitor.getPlatform();
    readonly env = environment;

    readonly isEnterprise = INST == 'enterprise';
    readonly isWeb = this.platform == 'web';
    readonly isIos = this.platform == 'ios';
    readonly isAndroid = this.platform == 'android';
    readonly isCef = platform == 'cef';
    readonly isRealWeb = this.isWeb && !this.isCef;
    readonly isProd = environment.env == 'prod';

    readonly store      = inject(StoreService);
    // readonly cache      = inject(CacheService);

    readonly mobile     = signal<boolean | undefined>(undefined);
    readonly userId     = signal<Guid | undefined>(undefined);
    readonly user       = signal<Person | undefined>(undefined);
    readonly orgId      = signal<Guid | undefined>(undefined);
    readonly org        = signal<Org | undefined>(undefined);
    readonly projectId  = signal<Guid | undefined>(undefined);
    readonly project    = signal<Project | undefined>(undefined);
    readonly isGuest    = signal<boolean | undefined>(undefined);
    readonly granted    = signal<SM<boolean>>({});
    readonly pGranted   = signal<SM<SM<boolean>>>({});

    readonly userOpts   = computed<SM<any>>(() => {
        const orgId = this.orgId();
        const user = this.user();
        return (orgId ? user?._orgMap[orgId]?.options : undefined) || {};
    });

    constructor() {
        console.warn('[BaseService] init:', getRandomId(8));
        if (this.store) {
            this.store.state('user').subscribe(state => {
                this.mobile.set(state.mobile || !this.isWeb);
                this.userId.set(state.userId);
                this.user.set(state.user);
                this.isGuest.set(state.isGuest);
            });
            this.store.granted.subscribe(granted => {
                this.granted.set(granted.global);
                this.pGranted.set(granted);
            });

            const orgId = this.store.activeOrgId.getValue();
            this.orgId.set(orgId);
            this.org.set(orgId ? this.store.getState('orgs').items[orgId] : undefined);
            this.store.activeOrgId.subscribe(id => {
                this.orgId.set(id);
                this.org.set(id ? this.store.getState('orgs').items[id] : undefined);
            });
            this.store.state('orgs').subscribe(st => this.org.set(st.items[this.orgId()!]));

            const projectId = this.store.activeProjectId.getValue();
            const pid = projectId == '-' ? undefined : projectId;
            this.projectId.set(pid);
            this.project.set(pid ? this.store.getState('projects').items[pid] : undefined);
            this.store.activeProjectId.subscribe(id => {
                this.projectId.set(id);
                this.project.set(id ? this.store.getState('projects').items[id] : undefined);
            });
            this.store.state('projects').subscribe(st => this.project.set(st.items[this.projectId()!]));
        }
    }

}
